import { useEffect, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { decodeRoles, initializeSession } from '../../lib/auth'
import type { Lang } from '../../lib/i18n'
import { CommandHeader } from '../../components/CommandHeader'
import { st } from '../staff/staff-i18n'
import {
  amountText,
  canRaiseCashOut,
  CashOutApiError,
  raiseCashOut,
  type CashOutRequest,
  type PayeeKind,
} from '../../lib/cash-outs-api'
import { cx, type CashOutKey } from './cash-outs-i18n'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

const BUTTON_BASE =
  'inline-flex items-center justify-center rounded-md px-4 text-sm font-semibold min-h-[44px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
const BUTTON_PRIMARY = `${BUTTON_BASE} bg-primary text-primary-foreground hover:bg-primary/90`
const TOGGLE_ON = `${BUTTON_BASE} border border-primary bg-primary text-primary-foreground hover:bg-primary/90`
const TOGGLE_OFF = `${BUTTON_BASE} border border-border bg-card text-foreground hover:bg-secondary`
const INPUT =
  'w-full rounded-md border border-input bg-card px-3 text-sm text-foreground min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed'
const LABEL = 'block text-sm font-medium text-foreground mb-1'

type AuthState = 'checking' | 'ok' | 'not-allowed' | 'redirect'
type SendState = { kind: 'idle' } | { kind: 'sending' } | { kind: 'failed'; message: string }

const KINDS: ReadonlyArray<{ kind: PayeeKind; label: CashOutKey }> = [
  { kind: 'staff', label: 'kindStaff' },
  { kind: 'external', label: 'kindExternal' },
]

// R572 (c), Rook #68969 and #69106: the owner or an admin raises a cash out on HOP; the till
// pays it (R572 (b2)). One form: payee kind, name, amount in dollars and cents, reason. The list
// below it is what was raised on this page, from each POST's own answer (no read: the open list
// is the till's).
export function CashOutScreen({ lang, onLangToggle, isOnline }: Props) {
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [payeeKind, setPayeeKind] = useState<PayeeKind | null>(null)
  const [payeeText, setPayeeText] = useState('')
  const [amountTyped, setAmountTyped] = useState('')
  const [reason, setReason] = useState('')
  const [send, setSend] = useState<SendState>({ kind: 'idle' })
  const [raised, setRaised] = useState<CashOutRequest[]>([])

  useEffect(() => {
    initializeSession().then((session) => {
      if (!session.authenticated) return setAuthState('redirect')
      setAuthState(canRaiseCashOut(decodeRoles()) ? 'ok' : 'not-allowed')
    })
  }, [])

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const amount = amountText(amountTyped)
  const sending = send.kind === 'sending'
  let disabledReason: string | null = null
  if (!sending) {
    if (!isOnline) disabledReason = st(lang, 'needsConnection')
    else if (payeeKind === null) disabledReason = cx(lang, 'needKind')
    else if (payeeText.trim() === '') disabledReason = cx(lang, 'needName')
    else if (amount === null) disabledReason = cx(lang, 'needAmount')
    else if (reason.trim() === '') disabledReason = cx(lang, 'needReason')
  }
  const canSend = !sending && disabledReason === null

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // A refused amount, a missing field, offline or already sending: nothing is sent.
    if (!canSend || payeeKind === null || amount === null) return
    setSend({ kind: 'sending' })
    try {
      const request = await raiseCashOut({ payeeKind, payeeText: payeeText.trim(), amountUsd: amount, reason: reason.trim() })
      setRaised((prev) => [request, ...prev])
      setPayeeKind(null)
      setPayeeText('')
      setAmountTyped('')
      setReason('')
      setSend({ kind: 'idle' })
    } catch (err) {
      let message: string
      if (err instanceof CashOutApiError) {
        // A 400 or a 409 carries the server's own words, which name the refusal.
        if (err.status === 403) message = cx(lang, 'error403')
        else if (err.status >= 400 && err.status < 500) message = err.message
        else message = cx(lang, 'errorOther')
      } else {
        message = cx(lang, 'errorNetwork')
      }
      setSend({ kind: 'failed', message })
    }
  }

  const dir = lang === 'ar' ? 'rtl' : 'ltr'
  const last = raised[0]

  return (
    <div dir={dir} className="min-h-screen bg-background font-sans">
      <CommandHeader title={cx(lang, 'title')} lang={lang} onLangToggle={onLangToggle} isOnline={isOnline} />
      <main className="px-4 py-6 max-w-2xl mx-auto pb-10 space-y-6">
        {authState === 'not-allowed' && (
          <p className="rounded-md border border-border bg-card px-4 py-6 text-center text-sm text-foreground" data-testid="cash-out-not-allowed">
            {cx(lang, 'ownerAdminOnly')}
          </p>
        )}

        {authState === 'ok' && (
          <>
            <div className="space-y-1 text-sm text-muted-foreground" data-testid="cash-out-intro">
              <p>{cx(lang, 'intro')}</p>
              <p data-testid="cash-out-quiet-day">{cx(lang, 'quietDay')}</p>
            </div>

            <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-4 space-y-4" data-testid="cash-out-form">
              <fieldset>
                <legend className={LABEL}>{cx(lang, 'payeeKind')}</legend>
                <div className="flex flex-wrap gap-2">
                  {KINDS.map(({ kind, label }) => (
                    <button
                      key={kind}
                      type="button"
                      aria-pressed={payeeKind === kind}
                      data-testid={`cash-out-kind-${kind}`}
                      disabled={sending}
                      onClick={() => setPayeeKind(kind)}
                      className={payeeKind === kind ? TOGGLE_ON : TOGGLE_OFF}
                    >
                      {cx(lang, label)}
                    </button>
                  ))}
                </div>
              </fieldset>

              <div>
                <label htmlFor="cash-out-payee" className={LABEL}>
                  {cx(lang, 'payeeName')}
                </label>
                <input
                  id="cash-out-payee"
                  type="text"
                  value={payeeText}
                  maxLength={120}
                  disabled={sending}
                  onChange={(e) => setPayeeText(e.target.value)}
                  className={INPUT}
                />
              </div>

              <div>
                <label htmlFor="cash-out-amount" className={LABEL}>
                  {cx(lang, 'amount')}
                </label>
                <input
                  id="cash-out-amount"
                  type="text"
                  inputMode="decimal"
                  dir="ltr"
                  value={amountTyped}
                  disabled={sending}
                  aria-describedby="cash-out-amount-hint"
                  onChange={(e) => setAmountTyped(e.target.value)}
                  className={`${INPUT} text-end`}
                />
                <p id="cash-out-amount-hint" className="mt-1 text-xs text-muted-foreground">
                  {cx(lang, 'amountHint')}
                </p>
              </div>

              <div>
                <label htmlFor="cash-out-reason" className={LABEL}>
                  {cx(lang, 'reason')}
                </label>
                <textarea
                  id="cash-out-reason"
                  value={reason}
                  maxLength={300}
                  rows={2}
                  disabled={sending}
                  onChange={(e) => setReason(e.target.value)}
                  className={`${INPUT} py-2`}
                />
              </div>

              {send.kind === 'failed' && (
                <p role="alert" className="rounded-md border border-destructive/30 bg-destructive-tint px-3 py-2 text-sm text-destructive" data-testid="cash-out-error">
                  {send.message}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" disabled={!canSend} aria-busy={sending} data-testid="cash-out-submit" className={BUTTON_PRIMARY}>
                  {sending ? cx(lang, 'submitting') : cx(lang, 'submit')}
                </button>
                {disabledReason && (
                  <span className="text-xs text-muted-foreground" data-testid="cash-out-reason-disabled">
                    {disabledReason}
                  </span>
                )}
              </div>
            </form>

            {last && (
              <p role="status" className="text-sm font-medium text-sage-ink" data-testid="cash-out-raised">
                {cx(lang, 'raised', { payee: last.payeeText, amount: last.amountUsd })}
              </p>
            )}

            {raised.length > 0 && (
              <section className="space-y-2" data-testid="cash-out-raised-list">
                <h2 className="text-lg font-semibold text-foreground">{cx(lang, 'raisedList')}</h2>
                <ul className="space-y-2">
                  {raised.map((r) => (
                    <li key={r.id} data-cash-out-id={r.id} className="rounded-lg border border-border bg-card p-3 text-sm text-foreground">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-semibold">
                          {r.payeeText} ({cx(lang, r.payeeKind === 'staff' ? 'kindStaff' : 'kindExternal')})
                        </span>
                        <span dir="ltr" className="font-semibold tabular-nums">
                          ${r.amountUsd}
                        </span>
                      </div>
                      <p className="text-muted-foreground">{r.reason}</p>
                      <p className="text-xs text-muted-foreground">{cx(lang, 'statusOpen')}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  )
}
